import { useEffect, useMemo } from 'react';

import { TransactionType } from '@/modules/transaction/transaction.types';

import useGetClaimsPaymentDetails from './useGetClaimsPaymentDetails.query';
import useGetLockboxPaymentDetails from './useGetLockboxPaymentDetails.query';

interface Args {
  claimId?: string;
  postingId?: string;
  transactionType?: TransactionType;
}

export const useGetPaymentDetails = (params: Args) => {
  const isLockboxTransaction = useMemo(() => {
    if (!params.transactionType) return false;
    return [
      TransactionType.OUTBOUND_TRANSFER_OF_ASSETS,
      TransactionType.INBOUND_TRANSFER_OF_ASSETS,
      TransactionType.CLAIM_REPAYMENT,
    ].includes(params.transactionType);
  }, [params.transactionType]);

  const claimsQuery = useGetClaimsPaymentDetails({ claimId: params.claimId });
  const lockboxQuery = useGetLockboxPaymentDetails({
    postingId: params.postingId,
    isLockboxTransaction,
  });

  const refetch = () => {
    if (params.claimId) {
      return claimsQuery.refetch();
    }
    if (params.postingId && isLockboxTransaction) {
      return lockboxQuery.refetch();
    }
    return Promise.resolve(null);
  };

  useEffect(() => {
    if (params.claimId || (params.postingId && isLockboxTransaction)) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.claimId, params.postingId, isLockboxTransaction]);

  const activeQuery = params.claimId
    ? claimsQuery
    : params.postingId && isLockboxTransaction
    ? lockboxQuery
    : null;

  return {
    data: activeQuery?.data?.data,
    paymentDetails: (params.postingId && !isLockboxTransaction)
      ? []
      : activeQuery?.formattedData,
    directDepositInfo: activeQuery?.directDepositInfo,
    isLoading: activeQuery ? activeQuery.isLoading : false,
    isFetching: activeQuery ? activeQuery.isFetching : false,
    refetch,
    error: activeQuery ? activeQuery.error : null,
    isSuccess: activeQuery ? activeQuery.isSuccess : false,
  };
};
